import { useEffect, useRef, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { ensureSettings, useDiscardMedia, useSendMessage, useUploadMedia } from '../../hooks/queries'
import { merken, neuerSchluessel } from '../../lib/outbox'
import { ApiError, netzStand } from '../../api/client'
import type { Settings } from '../../api/types'
import { canPreviewDirectly, isImage, prepareImage } from '../../lib/images'
import { dauerText, grabPoster, isVideo, readVideoInfo, uploadVideo } from '../../lib/video'
import { groesse } from '../../lib/format'
import { PhotoIcon, SendIcon, VideoIcon, XIcon } from '../../components/Icons'
import type { Bezug } from './ThreadPage'

interface Props {
  tripId: number
  placeholder: string
  disabled?: boolean
  /**
   * Der geöffnete Antwortverlauf. Gesetzt heißt: was hier geschrieben wird,
   * geht an genau diesen Menschen — der Server macht daraus eine private
   * Nachricht.
   */
  withUser?: number
  /**
   * Was statt der Eingabezeile dasteht, wenn nicht geschrieben werden kann.
   *
   * Ohne Angabe: die abgeschlossene Reise, der ursprüngliche Fall. Der Grund
   * gehört an die Stelle, an der sonst das Schreibfeld wäre — ein Feld, das
   * nichts kann, verspricht etwas, das es nicht hält.
   */
  disabledHint?: React.ReactNode
  /**
   * Worauf sich die Nachricht bezieht — gesetzt an einer Sprechblase weiter
   * oben oder an einem Foto in der Großansicht.
   */
  bezug?: Bezug | null
  /** Den Bezug wieder loswerden: das ✕ am Zitat, und nach dem Senden. */
  onBezugWeg?: () => void
}

/**
 * `offline` ist die neue Stufe: das Bild ist fertig gerechnet, aber es gibt
 * kein Netz. Es liegt dann als Blob bereit und geht mit der Nachricht in den
 * Ausgangskorb, statt als Fehler dazustehen.
 */
type Stufe = 'wartet' | 'rechnet' | 'lädt' | 'fertig' | 'offline' | 'fehler'

/**
 * Ein ausgewähltes Foto auf seinem Weg zum Server.
 *
 * Der Schlüssel ist ein eigener: die id vom Server gibt es erst am Ende des
 * Weges, die Kachel aber schon am Anfang.
 */
interface Attachment {
  key: number
  preview: string | null
  stufe: Stufe
  /** Anteil des Hochladens, 0 bis 1. */
  fortschritt: number
  /**
   * Was die Datei wiegt -- und zwar von Anfang an.
   *
   * Der iOS-Teilen-Dialog merkt sich die Größenauswahl nicht (Format und
   * Standort schon). Von derselben Aufnahme kommt deshalb mal 25 MB und mal
   * 300 MB, je nachdem ob man daran gedacht hat. Wer das erst erfährt, wenn
   * der Server ablehnt, hat schon gewartet.
   */
  bytes: number
  art: 'bild' | 'video'
  /** Nur bei Videos, sobald gelesen. */
  dauerMs?: number
  /** Die Grenze, an der diese Datei scheitern wird -- in MB, sonst 0. */
  ueberGrenze?: number
  /**
   * Das fertig verkleinerte Bild.
   *
   * Bleibt liegen, bis die Nachricht raus ist: ohne Netz wandert es in den
   * Ausgangskorb, und dort steht das Bild selbst, nicht seine id vom Server.
   */
  blob?: Blob
  id?: number
  problem?: string
}

let naechsterSchluessel = 1

/**
 * Das Vorschaubild zum Bezug -- aus den Daten des offenen Chats.
 *
 * Hier ausnahmsweise aus dem Zwischenspeicher und nicht vom Server: das Zitat
 * über der Eingabezeile sieht **nur der, der gerade schreibt**, und zwar zu
 * einer Nachricht, die vor ihm im selben Verlauf steht. In der Sprechblase
 * einer gesendeten Antwort ist es umgekehrt -- dort liefert der Server den
 * Auszug, denn dort lesen andere mit.
 */
function bezugBild(bezug: Bezug): string | null {
  const bild = bezug.mediaId === undefined
    ? undefined
    : bezug.message.media.find((m) => m.id === bezug.mediaId)
  return bild?.poster_url ?? bild?.url ?? null
}

/** Die Eingabezeile: wächst mit dem Text, Enter sendet, Umschalt+Enter bricht um. */
export function Composer({
  tripId,
  placeholder,
  disabled,
  withUser,
  disabledHint,
  bezug,
  onBezugWeg,
}: Props) {
  const [text, setText] = useState('')
  const [fotos, setFotos] = useState<Attachment[]>([])
  const [problem, setProblem] = useState<string | null>(null)
  const [sendeWennFertig, setSendeWennFertig] = useState(false)
  const field = useRef<HTMLTextAreaElement>(null)
  const picker = useRef<HTMLInputElement>(null)

  // Die Uploads gehen nacheinander, aufgereiht an dieser Kette. Das nächste
  // Bild wird schon verkleinert, während das vorige über die Leitung geht --
  // Rechnen und Senden überlappen, und beides ist auf einem Telefon der
  // Flaschenhals.
  const kette = useRef<Promise<void>>(Promise.resolve())

  const send = useSendMessage(tripId, withUser)
  const upload = useUploadMedia(tripId)
  const discard = useDiscardMedia()
  const client = useQueryClient()

  /**
   * Ob diese Datei über der Grenze liegt -- und über welcher.
   *
   * Die Grenze gilt ohnehin auf dem Server; hier steht sie nur, damit man es
   * vor dem Warten sieht statt danach. Ohne bekannte Werte wird nichts
   * behauptet.
   */
  function ueberGrenze(
    bytes: number,
    art: 'bild' | 'video',
    werte: Settings | null,
  ): number | undefined {
    if (!werte) return undefined
    const grenze = art === 'video' ? werte.video_max_mb : werte.image_max_mb
    return bytes > grenze * 1024 * 1024 ? grenze : undefined
  }

  // „Beschäftigt" heißt: da läuft noch etwas, warte damit. Ein Bild, das auf
  // Netz wartet, läuft gerade **nicht** -- es wäre sonst für immer beschäftigt
  // und der Beitrag ginge nie in den Korb.
  const beschaeftigt = fotos.some((foto) => unterwegs(foto.stufe))
  // Abgeleitet und nicht gemerkt, und das ist die Korrektur eines Fehlers:
  // vorher stand hier ein eigener Zustand, der beim Auswählen eines Videos auf
  // `true` ging und **nirgends** zurück. Der Hinweis blieb damit stehen, auch
  // nachdem die Nachricht längst raus war -- bis jemand den Chat verließ und
  // neu betrat. Was aus den Anhängen ablesbar ist, gehört nicht daneben noch
  // einmal aufgeschrieben.
  const hatVideo = fotos.some((foto) => foto.art === 'video')
  const fertige = fotos.filter((foto) => foto.stufe === 'fertig')

  function grow() {
    const element = field.current
    if (!element) return
    // Erst zurücksetzen, sonst wächst das Feld beim Löschen nicht mit.
    element.style.height = 'auto'
    element.style.height = `${Math.min(element.scrollHeight, 140)}px`
  }

  function aendere(key: number, teil: Partial<Attachment>) {
    setFotos((alt) => alt.map((foto) => (foto.key === key ? { ...foto, ...teil } : foto)))
  }

  /**
   * Fotos annehmen -- und zwar sofort.
   *
   * Für jede Datei steht die Kachel da, bevor irgendetwas gerechnet ist. Das
   * ist der ganze Punkt: der Auswahldialog gibt die Bilder ab und man ist
   * zurück im Chat, statt vor einem stehengebliebenen Bildschirm zu warten,
   * bis fünf iPhone-Fotos verkleinert sind.
   */
  async function add(files: FileList | null) {
    if (!files || files.length === 0) return
    setProblem(null)

    const aufgaben = Array.from(files).map((file) => ({
      file,
      key: naechsterSchluessel++,
    }))

    setFotos((alt) => [
      ...alt,
      ...aufgaben.map(({ file, key }) => ({
        key,
        preview: canPreviewDirectly(file) ? URL.createObjectURL(file) : null,
        stufe: 'wartet' as Stufe,
        fortschritt: 0,
        bytes: file.size,
        art: (isVideo(file) ? 'video' : 'bild') as 'bild' | 'video',
      })),
    ])

    // Erst die Werte, dann rechnen. Ein Foto, das mit der Vorgabe statt der
    // eingestellten Größe verkleinert wurde, fällt niemandem auf -- es sieht
    // ja aus wie ein Foto. Deshalb wird hier gewartet, und zwar nur beim
    // ersten Mal: danach steht die Antwort im Zwischenspeicher.
    const werte = await ensureSettings(client)

    // Ein Video geht unverändert raus; ob es durchgeht, steht damit schon
    // jetzt fest. Bei einem Foto entscheidet erst das verkleinerte Bild.
    for (const { file, key } of aufgaben) {
      if (isVideo(file)) {
        aendere(key, { ueberGrenze: ueberGrenze(file.size, 'video', werte) })
      }
    }

    for (const { file, key } of aufgaben) {
      // Ein Video geht einen eigenen Weg: nicht verkleinert (der Browser
      // könnte es, aber es dauerte Minuten und macht das Telefon heiß),
      // sondern in Stücken hochgeladen. Das Standbild entsteht dabei.
      if (isVideo(file)) {
        // Standbild und Dauer noch vor dem ersten Byte: die Kachel zeigt
        // damit, *was* da gleich rausgeht -- ein Bild statt eines grauen
        // Kastens, und die Länge daneben. Das Standbild wird weitergereicht,
        // damit es nicht zweimal gerechnet wird.
        const vorschau = grabPoster(file)
          .then((blob) => {
            if (blob !== null) aendere(key, { preview: URL.createObjectURL(blob) })
            return blob
          })
          .catch(() => null)
        void readVideoInfo(file)
          .then((info) => aendere(key, { dauerMs: info.durationMs }))
          .catch(() => undefined)

        kette.current = kette.current.then(async () => {
          try {
            aendere(key, { stufe: 'lädt', fortschritt: 0 })
            const id = await uploadVideo(
              tripId,
              file,
              (anteil) => aendere(key, { fortschritt: anteil }),
              // Die Zusage, nicht das Bild: `await` an dieser Stelle hielte
              // den Upload auf, bis das Standbild da ist -- und wenn es nie
              // kommt, für immer.
              vorschau,
            )
            aendere(key, { stufe: 'fertig', fortschritt: 1, id })
          } catch (fehler) {
            aendere(key, {
              stufe: 'fehler',
              problem: fehler instanceof Error ? fehler.message : 'Das Video ging nicht durch.',
            })
          }
        })
        continue
      }

      if (!isImage(file)) {
        aendere(key, { stufe: 'fehler', problem: 'Das ist weder ein Bild noch ein Video.' })
        continue
      }

      try {
        aendere(key, { stufe: 'rechnet' })
        const prepared = await prepareImage(
          file,
          werte ? { maxEdge: werte.image_max_edge, quality: werte.image_quality } : undefined,
        )
        // Ab hier zählt das verkleinerte Bild, nicht die Datei aus der
        // Auswahl: das ist die Zahl, die über die Leitung geht.
        aendere(key, {
          bytes: prepared.blob.size,
          ueberGrenze: ueberGrenze(prepared.blob.size, 'bild', werte),
          // Das Bild bleibt hier liegen -- ohne Netz ist es das, was in den
          // Ausgangskorb wandert.
          blob: prepared.blob,
        })

        // Ein Bild ohne eigene Vorschau (HEIC) bekommt sie jetzt.
        setFotos((alt) =>
          alt.map((foto) =>
            foto.key === key && foto.preview === null
              ? { ...foto, preview: URL.createObjectURL(prepared.blob) }
              : foto,
          ),
        )

        kette.current = kette.current.then(async () => {
          // Ohne Netz gar nicht erst anfangen: das Bild ist fertig und wartet
          // mit der Nachricht auf später. Gefragt wird `netzStand()` und nicht
          // `navigator.onLine` -- Letzteres sagt „online", sobald irgendein
          // WLAN da ist, auch wenn nichts hindurchgeht.
          if (!netzStand()) {
            aendere(key, { stufe: 'offline' })
            return
          }

          try {
            aendere(key, { stufe: 'lädt', fortschritt: 0 })
            const media = await upload.mutateAsync({
              blob: prepared.blob,
              // Aus dem Original gelesen, bevor verkleinert wurde -- der
              // Server legt Ort und Zeit an der Datei ab.
              aufnahme: prepared.aufnahme,
              onProgress: (anteil) => aendere(key, { fortschritt: anteil }),
            })
            aendere(key, { stufe: 'fertig', fortschritt: 1, id: media.id })
          } catch (fehler) {
            // Am Netz gescheitert heißt: später. Alles andere ist ein Fehler,
            // den man sehen muss -- ein Bild über der Grenze etwa geht auch
            // beim zehnten Versuch nicht durch.
            if (fehler instanceof ApiError && fehler.offline) {
              aendere(key, { stufe: 'offline' })
              return
            }
            // Ein gescheitertes Bild hält die anderen nicht auf.
            aendere(key, {
              stufe: 'fehler',
              problem: fehler instanceof Error ? fehler.message : 'Das Bild ging nicht durch.',
            })
          }
        })
      } catch (fehler) {
        aendere(key, {
          stufe: 'fehler',
          problem: fehler instanceof Error ? fehler.message : 'Das Bild ließ sich nicht öffnen.',
        })
      }
    }
  }

  function remove(item: Attachment) {
    setFotos((alt) => alt.filter((foto) => foto.key !== item.key))
    if (item.preview) URL.revokeObjectURL(item.preview)
    // Auf dem Server auch wegräumen, sonst zählt es gegen das Kontingent,
    // bis es von selbst verfällt.
    if (item.id !== undefined) discard.mutate(item.id)
  }

  /** Nach dem Absenden: Feld leer, Kacheln weg, Vorschauen freigegeben. */
  function aufraeumen() {
    setText('')
    fotos.forEach((foto) => foto.preview && URL.revokeObjectURL(foto.preview))
    setFotos([])
    setProblem(null)
    // Der Bezug gilt für diese eine Nachricht. Bliebe er stehen, hinge die
    // nächste ungefragt am selben Beitrag.
    onBezugWeg?.()
    requestAnimationFrame(grow)
  }

  /**
   * In den Ausgangskorb legen, statt zu verlieren.
   *
   * Mitgegeben werden die **Bilder**, nicht ihre ids -- auch die schon
   * hochgeladenen. Ein Foto, das vor dem Netzabbruch durchkam, hat zwar eine
   * id, aber die räumt der Server nach einem Tag weg; ein Eintrag, der morgen
   * früh darauf zeigt, wäre kaputt. Lieber einmal doppelt hochladen.
   */
  function inDenKorb(body: string, clientKey: string) {
    void merken({
      tripId,
      withUser,
      body,
      clientKey,
      // Der Bezug muss den Korb überstehen: ohne ihn käme die Antwort später
      // ohne ihren Anfang heraus, und niemand wüsste mehr, worauf sie ging.
      replyToId: bezug?.message.id,
      replyToMediaId: bezug?.mediaId,
      bilder: fotos.map((foto) => foto.blob).filter((blob): blob is Blob => blob !== undefined),
    }).then(aufraeumen)
  }

  function submit() {
    const body = text.trim()
    if (!body && fotos.length === 0) return
    if (send.isPending) return

    // Noch nicht fertig: nicht abweisen, sondern vormerken. Wer unterwegs
    // Fotos schickt, soll das Telefon weglegen können.
    if (beschaeftigt) {
      setSendeWennFertig(true)
      return
    }

    setSendeWennFertig(false)

    // Ein Schlüssel für diese eine Nachricht, gleich hier vergeben: geht der
    // Versuch am Netz kaputt, wandert **derselbe** in den Korb. Sonst stünde
    // der Beitrag doppelt da, wenn der Server ihn längst hatte und nur die
    // Antwort verlorenging.
    const clientKey = neuerSchluessel()

    // Ohne Netz -- oder mit einem Bild, das deshalb liegengeblieben ist --
    // wird gar nicht erst versucht.
    if (!netzStand() || fotos.some((foto) => foto.stufe === 'offline')) {
      // Ein Video wartet nicht im Korb: zweihundert Megabyte gehören nicht in
      // IndexedDB, und stillschweigend weglassen wäre schlimmer als absagen.
      if (fotos.some((foto) => foto.art === 'video')) {
        setProblem(
          'Ein Video braucht Netz. Nimm es heraus — der Text und die Fotos gehen dann später von selbst raus.',
        )
        return
      }
      inDenKorb(body, clientKey)
      return
    }

    if (!body && fertige.length === 0) return

    send.mutate(
      {
        body,
        mediaIds: fertige.map((foto) => foto.id!),
        clientKey,
        replyToId: bezug?.message.id,
        replyToMediaId: bezug?.mediaId,
      },
      {
        onSuccess: aufraeumen,
        onError: (fehler) => {
          // Am Netz gescheitert: nicht verloren, sondern verschoben. Eine
          // Absage des Servers dagegen bleibt eine Absage -- sie stünde im
          // Korb bis in alle Ewigkeit.
          if (fehler instanceof ApiError && fehler.offline) inDenKorb(body, clientKey)
        },
      },
    )
  }

  // Der vorgemerkte Versand, sobald das letzte Bild durch ist. Ein
  // gescheitertes Bild nimmt die Vormerkung zurück -- sonst ginge der Beitrag
  // ohne das Foto raus, das man eigentlich zeigen wollte.
  useEffect(() => {
    if (!sendeWennFertig || beschaeftigt) return
    if (fotos.some((foto) => foto.stufe === 'fehler')) {
      setSendeWennFertig(false)
      setProblem('Ein Foto kam nicht durch. Schau es dir an und schick es dann ab.')
      return
    }
    setSendeWennFertig(false)
    submit()
    // submit hängt an Text und Fotos; die stehen zu diesem Zeitpunkt fest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendeWennFertig, beschaeftigt])

  if (disabled) {
    return <p className="composer__hint">{disabledHint ?? 'Diese Reise ist abgeschlossen.'}</p>
  }

  const wartet = fotos.filter((foto) => unterwegs(foto.stufe)).length

  return (
    <div className="composer-area">
      {/* Worauf das hier antwortet -- direkt über dem Feld, in dem man
          schreibt. Weiter oben, bei der Sprechblase selbst, wäre es beim
          Tippen längst aus dem Bild. */}
      {bezug && (
        <div className="bezug">
          {bezugBild(bezug) && <img className="bezug__bild" src={bezugBild(bezug)!} alt="" />}
          <span className="bezug__text">
            <span className="bezug__autor">
              Antwort an {bezug.message.author.display_name}
              {bezug.mediaId !== undefined && ' · Foto'}
            </span>
            <span className="bezug__auszug">
              {bezug.message.body !== '' ? bezug.message.body : 'Foto'}
            </span>
          </span>
          <button
            type="button"
            className="icon-button bezug__weg"
            title="Bezug entfernen"
            aria-label="Bezug entfernen"
            onClick={() => onBezugWeg?.()}
          >
            <XIcon size={16} />
          </button>
        </div>
      )}

      {(fotos.length > 0 || problem) && (
        <div className="attachments">
          {fotos.map((foto) => (
            <Kachel key={foto.key} foto={foto} onRemove={() => remove(foto)} />
          ))}

          {problem && <span className="attachments__problem">{problem}</span>}
        </div>
      )}

      {(wartet > 0 || sendeWennFertig) && (
        <p className="composer__status" role="status">
          {sendeWennFertig
            ? 'Wird gesendet, sobald alles durch ist …'
            : `${fertige.length} von ${fotos.length} fertig …`}
        </p>
      )}

      {/* Ein Knopfdruck, auf den nichts folgt, ist das Schlimmste, was eine
          Eingabezeile tun kann -- man drückt noch einmal und noch einmal. Der
          Server schickt in jedem dieser Fälle einen brauchbaren Satz; er muss
          nur ankommen. */}
      {send.isError && !(send.error instanceof ApiError && send.error.offline) && (
        <p className="composer__status composer__status--fehler" role="alert">
          {send.error instanceof Error ? send.error.message : 'Die Nachricht ging nicht raus.'}
        </p>
      )}

      {/* Der einzige Hebel gegen ein Video, das nur auf Apple-Geräten läuft,
          liegt beim Absender -- und zwar bevor er es auswählt. Deshalb steht
          der Satz hier und nicht in einer Fußnote. */}
      {hatVideo && (
        <p className="composer__status">
          Videos gehen unverändert raus. Wähle beim Teilen auf dem iPhone
          <strong> „Am kompatibelsten"</strong>, damit sie auch am Rechner und
          auf Android laufen.
        </p>
      )}

      <div className="composer">
        <input
          ref={picker}
          type="file"
          accept="image/*,video/*"
          multiple
          hidden
          onChange={(event) => {
            void add(event.target.files)
            // Zurücksetzen, sonst löst dieselbe Datei kein zweites Mal aus.
            event.target.value = ''
          }}
        />
        <button
          type="button"
          className="composer__attach"
          onClick={() => picker.current?.click()}
          aria-label="Fotos und Videos hinzufügen"
          title="Fotos und Videos hinzufügen"
        >
          <PhotoIcon size={20} />
        </button>

        <textarea
          ref={field}
          className="composer__input"
          rows={1}
          value={text}
          placeholder={placeholder}
          aria-label={placeholder}
          onChange={(event) => {
            setText(event.target.value)
            grow()
          }}
          onKeyDown={(event) => {
            // Auf einem Telefon gibt es keine Umschalt-Taste im Blickfeld;
            // dort bleibt die Eingabetaste ein Zeilenumbruch und gesendet wird
            // über den Knopf.
            if (event.key === 'Enter' && !event.shiftKey && !isTouch()) {
              event.preventDefault()
              submit()
            }
          }}
        />
        <button
          type="button"
          className="composer__send"
          disabled={(!text.trim() && fotos.length === 0) || send.isPending || sendeWennFertig}
          onClick={submit}
          aria-label="Senden"
          title="Senden"
        >
          <SendIcon size={20} />
        </button>
      </div>
    </div>
  )
}

/**
 * Eine Kachel je Foto -- mit dem Stand der Dinge darauf.
 *
 * Vorbereiten und Hochladen sind zwei verschiedene Wartezeiten: das eine
 * hängt am Telefon, das andere am Netz. Wer unterwegs steht und sich fragt,
 * ob es am Empfang liegt, sieht es hier.
 */
/** Ob an dieser Kachel gerade gearbeitet wird -- Warten auf Netz zählt nicht. */
function unterwegs(stufe: Stufe): boolean {
  return stufe === 'wartet' || stufe === 'rechnet' || stufe === 'lädt'
}

function Kachel({ foto, onRemove }: { foto: Attachment; onRemove: () => void }) {
  const laeuft = unterwegs(foto.stufe)

  return (
    <div className="attachment-item">
    <div
      className={foto.stufe === 'fehler' ? 'attachment attachment--fehler' : 'attachment'}
      title={foto.problem ?? undefined}
    >
      {foto.preview ? (
        <img src={foto.preview} alt="" className={laeuft ? 'attachment__blass' : undefined} />
      ) : (
        <span className="attachment__leer">
          {/* Ohne Standbild soll wenigstens dastehen, um was es sich
              handelt -- ein Bildsymbol an einem Video führt in die Irre. */}
          {foto.art === 'video' ? <VideoIcon size={18} /> : <PhotoIcon size={18} />}
        </span>
      )}

      {laeuft && (
        <span className="attachment__stufe">
          {foto.stufe === 'lädt' ? `${Math.round(foto.fortschritt * 100)} %` : '…'}
        </span>
      )}

      {/* Fertig gerechnet, aber ohne Netz: die Kachel sagt, dass sie wartet --
          sonst sähe sie aus wie eine, die durch ist. */}
      {foto.stufe === 'offline' && (
        <span className="attachment__stufe" title="Wartet auf Netz">
          ⏳
        </span>
      )}

      {foto.stufe === 'lädt' && (
        <span className="attachment__balken">
          <span style={{ width: `${Math.round(foto.fortschritt * 100)}%` }} />
        </span>
      )}

      {foto.stufe === 'fehler' && <span className="attachment__stufe">!</span>}

      {/* Solange es läuft, gibt es nichts wegzunehmen -- der Upload wäre
          ohnehin schon unterwegs. */}
      {!laeuft && (
        <button
          type="button"
          className="attachment__remove"
          onClick={onRemove}
          aria-label="Foto wieder entfernen"
          title="Entfernen"
        >
          <XIcon size={13} />
        </button>
      )}
    </div>

    {/* Was da rausgeht, in Zahlen. Bei einem verkleinerten Foto ist es eine
        Beruhigung, bei einem Video aus dem Teilen-Dialog manchmal eine
        Warnung -- und beides sieht man besser vorher als hinterher. */}
    <span
      className={
        foto.ueberGrenze ? 'attachment__groesse attachment__groesse--zuviel' : 'attachment__groesse'
      }
      title={foto.ueberGrenze ? `Die Grenze liegt bei ${foto.ueberGrenze} MB.` : undefined}
    >
      {groesse(foto.bytes)}
      {foto.art === 'video' && foto.dauerMs !== undefined && foto.dauerMs > 0
        ? ` · ${dauerText(foto.dauerMs)}`
        : ''}
      {/* Nicht erst am Ende des Uploads: die Zahl steht ohnehin da, und
          daneben passt der Grund, warum sie zu hoch ist. */}
      {foto.ueberGrenze ? ` · über ${foto.ueberGrenze} MB` : ''}
    </span>
    </div>
  )
}

function isTouch(): boolean {
  return window.matchMedia('(pointer: coarse)').matches
}
